import { SettingsKey } from "./setting.model";
import { IReportSettings } from "./report.model";
import type { ITenantReportOption } from "../services/api/reports-api.service";

/** Settings interface */
export interface ReportOption {
  key: string;
  value: string | boolean | number;
  tenantOptionId: number | -1;
}

export enum ReportNotesPosition {
  cover = "cover",
  insideCover = "inside-cover",
  afterContents = "after-contents",
}

export enum ReportDefectBlockOrientation {
  standard = "standard",
  inline = "inline",
}

export type ReportType = "pdf" | "docx" | "csv";

/**
 * Maintains list of settings related to reports
 */
export class ISettingsReportOptions {
  constructor() {}

  // Text based settings
  company: ReportOption = {
    key: SettingsKey.Company,
    value: "",
    tenantOptionId: -1,
  };
  firstname: ReportOption = {
    key: SettingsKey.Firstname,
    value: "",
    tenantOptionId: -1,
  };
  lastname: ReportOption = {
    key: SettingsKey.Lastname,
    value: "",
    tenantOptionId: -1,
  };
  position: ReportOption = {
    key: SettingsKey.Position,
    value: "",
    tenantOptionId: -1,
  };
  reportTextTitle: ReportOption = {
    key: SettingsKey.ReportTextTitle,
    value: "DEFECT REPORT",
    tenantOptionId: -1,
  };
  reportTextNotes: ReportOption = {
    key: SettingsKey.ReportTextNotes,
    value: "",
    tenantOptionId: -1,
  };
  // String `ReportNotesPosition`
  reportNotesPosition: ReportOption = {
    key: SettingsKey.ReportNotesPosition,
    /** use `ReportNotesPosition` */
    value: ReportNotesPosition.insideCover,
    tenantOptionId: -1,
  };
  // Bool based settings
  reportShowCompanyName: ReportOption = {
    key: SettingsKey.ReportShowCompanyName,
    value: true,
    tenantOptionId: -1,
  };
  reportShowProjectImage: ReportOption = {
    key: SettingsKey.ReportShowProjectImage,
    value: true,
    tenantOptionId: -1,
  };
  reportShowProjectRef: ReportOption = {
    key: SettingsKey.ReportShowProjectRef,
    value: true,
    tenantOptionId: -1,
  };
  reportShowAddress: ReportOption = {
    key: SettingsKey.ReportShowAddress,
    value: true,
    tenantOptionId: -1,
  };
  reportShowClient: ReportOption = {
    key: SettingsKey.ReportShowClient,
    value: true,
    tenantOptionId: -1,
  };
  reportShowPreparedBy: ReportOption = {
    key: SettingsKey.ReportShowPreparedBy,
    value: true,
    tenantOptionId: -1,
  };
  reportShowNotes: ReportOption = {
    key: SettingsKey.ReportShowNotes,
    value: true,
    tenantOptionId: -1,
  };
  reportShowSignatures: ReportOption = {
    key: SettingsKey.ReportShowSignatures,
    value: false,
    tenantOptionId: -1,
  };

  // Accent colors
  reportAccentColor: ReportOption = {
    key: SettingsKey.ReportAccentColor,
    value: "#3880ff",
    tenantOptionId: -1,
  };

  // Defect page options
  reportShowDefectOrientation: ReportOption = {
    key: SettingsKey.ReportShowDefectOrientation,
    value: ReportDefectBlockOrientation.standard,
    tenantOptionId: -1,
  };
  reportShowDefectPhotoMeta: ReportOption = {
    key: SettingsKey.ReportShowDefectPhotoMeta,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectNumber: ReportOption = {
    key: SettingsKey.ReportShowDefectNumber,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectStatus: ReportOption = {
    key: SettingsKey.ReportShowDefectStatus,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectPriority: ReportOption = {
    key: SettingsKey.ReportShowDefectPriority,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectDueDate: ReportOption = {
    key: SettingsKey.ReportShowDefectDuedate,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectArea: ReportOption = {
    key: SettingsKey.ReportShowDefectArea,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectElement: ReportOption = {
    key: SettingsKey.ReportShowDefectElement,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectIssue: ReportOption = {
    key: SettingsKey.ReportShowDefectIssue,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectAssignment: ReportOption = {
    key: SettingsKey.ReportShowDefectAssignment,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectComments: ReportOption = {
    key: SettingsKey.ReportShowDefectComments,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectActivity: ReportOption = {
    key: SettingsKey.ReportShowDefectActivity,
    value: true,
    tenantOptionId: -1,
  };
  reportShowDefectQrCode: ReportOption = {
    key: SettingsKey.ReportShowDefectQrCode,
    value: true,
    tenantOptionId: -1,
  };

  // Number
  reportQuality: ReportOption = {
    key: SettingsKey.ReportQuality,
    value: IReportSettings.DEFAULT_IMAGE_QUALITY,
    tenantOptionId: -1,
  };

  /**
   * Populate report options from Settings[] array
   * @param {Settings[]} settings
   */
  fromApi(settings: ITenantReportOption[]): void {
    settings.forEach((o: ITenantReportOption) => {
      // Text based settings (FREE)
      if (o.name === SettingsKey.Company) {
        this.company.value = o.value;
        this.company.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.Firstname) {
        this.firstname.value = o.value;
        this.firstname.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.Lastname) {
        this.lastname.value = o.value;
        this.lastname.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.Position) {
        this.position.value = o.value;
        this.position.tenantOptionId = o.tenantOptionId;
      }
      // Text based settings
      if (o.name === SettingsKey.ReportTextTitle) {
        this.reportTextTitle.value = o.value;
        this.reportTextTitle.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportTextNotes) {
        this.reportTextNotes.value = o.value;
        this.reportTextNotes.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportNotesPosition) {
        this.reportNotesPosition.value = o.value;
        this.reportNotesPosition.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportAccentColor) {
        this.reportAccentColor.value = o.value;
        this.reportAccentColor.tenantOptionId = o.tenantOptionId;
      }
      // Bool based settings
      if (o.name === SettingsKey.ReportShowCompanyName) {
        this.reportShowCompanyName.value = o.value != "0";
        this.reportShowCompanyName.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowProjectImage) {
        this.reportShowProjectImage.value = o.value != "0";
        this.reportShowProjectImage.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowProjectRef) {
        this.reportShowProjectRef.value = o.value != "0";
        this.reportShowProjectRef.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowAddress) {
        this.reportShowAddress.value = o.value != "0";
        this.reportShowAddress.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowClient) {
        this.reportShowClient.value = o.value != "0";
        this.reportShowClient.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowPreparedBy) {
        this.reportShowPreparedBy.value = o.value != "0";
        this.reportShowPreparedBy.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowNotes) {
        this.reportShowNotes.value = o.value != "0";
        this.reportShowNotes.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowSignatures) {
        this.reportShowSignatures.value = o.value != "0";
        this.reportShowSignatures.tenantOptionId = o.tenantOptionId;
      }
      // defect pages
      if (o.name === SettingsKey.ReportShowDefectOrientation) {
        this.reportShowDefectOrientation.value =
          o.value || ReportDefectBlockOrientation.standard;
        this.reportShowDefectOrientation.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectPhotoMeta) {
        this.reportShowDefectPhotoMeta.value = o.value != "0";
        this.reportShowDefectPhotoMeta.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectNumber) {
        this.reportShowDefectNumber.value = o.value != "0";
        this.reportShowDefectNumber.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectStatus) {
        this.reportShowDefectStatus.value = o.value != "0";
        this.reportShowDefectStatus.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectPriority) {
        this.reportShowDefectPriority.value = o.value != "0";
        this.reportShowDefectPriority.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectDuedate) {
        this.reportShowDefectDueDate.value = o.value != "0";
        this.reportShowDefectDueDate.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectArea) {
        this.reportShowDefectArea.value = o.value != "0";
        this.reportShowDefectArea.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectElement) {
        this.reportShowDefectElement.value = o.value != "0";
        this.reportShowDefectElement.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectIssue) {
        this.reportShowDefectIssue.value = o.value != "0";
        this.reportShowDefectIssue.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectAssignment) {
        this.reportShowDefectAssignment.value = o.value != "0";
        this.reportShowDefectAssignment.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectComments) {
        this.reportShowDefectComments.value = o.value != "0";
        this.reportShowDefectComments.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectActivity) {
        this.reportShowDefectActivity.value = o.value != "0";
        this.reportShowDefectActivity.tenantOptionId = o.tenantOptionId;
      }
      if (o.name === SettingsKey.ReportShowDefectQrCode) {
        this.reportShowDefectQrCode.value = o.value != "0";
        this.reportShowDefectQrCode.tenantOptionId = o.tenantOptionId;
      }

      // Misc. settings
      if (o.name === SettingsKey.ReportQuality) {
        let _num = Number(o.value);
        this.reportQuality.value = !Number.isNaN(_num)
          ? _num
          : IReportSettings.DEFAULT_IMAGE_QUALITY;
        this.reportQuality.tenantOptionId = o.tenantOptionId;
      }
    });
  }

  /**
   * Set the tenant id against an option after it is set first time
   *
   * @param tenantOptionId
   * @param key
   */
  setTenantOptionId(tenantOptionId: number, key: string): void {
    switch (key) {
      // Text based settings (FREE)
      case SettingsKey.Company:
        this.company.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.Firstname:
        this.firstname.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.Lastname:
        this.lastname.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.Position:
        this.position.tenantOptionId = tenantOptionId;
        break;
      // Text based settings
      case SettingsKey.ReportTextTitle:
        this.reportTextTitle.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportTextNotes:
        this.reportTextNotes.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportNotesPosition:
        this.reportNotesPosition.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportAccentColor:
        this.reportAccentColor.tenantOptionId = tenantOptionId;
        break;
      // Bool based settings
      case SettingsKey.ReportShowCompanyName:
        this.reportShowCompanyName.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowProjectImage:
        this.reportShowProjectImage.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowProjectRef:
        this.reportShowProjectRef.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowAddress:
        this.reportShowAddress.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowClient:
        this.reportShowClient.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowPreparedBy:
        this.reportShowPreparedBy.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowNotes:
        this.reportShowNotes.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowSignatures:
        this.reportShowSignatures.tenantOptionId = tenantOptionId;
        break;
      // defect pages
      case SettingsKey.ReportShowDefectOrientation:
        this.reportShowDefectOrientation.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectPhotoMeta:
        this.reportShowDefectPhotoMeta.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectNumber:
        this.reportShowDefectNumber.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectStatus:
        this.reportShowDefectStatus.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectPriority:
        this.reportShowDefectPriority.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectDuedate:
        this.reportShowDefectDueDate.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectArea:
        this.reportShowDefectArea.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectElement:
        this.reportShowDefectElement.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectIssue:
        this.reportShowDefectIssue.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectAssignment:
        this.reportShowDefectAssignment.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectComments:
        this.reportShowDefectComments.tenantOptionId = tenantOptionId;
        break;
      case SettingsKey.ReportShowDefectActivity:
        this.reportShowDefectActivity.tenantOptionId = tenantOptionId;
        break;
      // handle unknown keys with default case
      default:
        console.warn(`Unknown key ${key} passed to updateDatabaseId function.`);
        break;
    }
  }
}
